import {z} from 'zod';
import dedent from 'dedent';
import {prepareEditArguments} from '@packages/utilities/exact-text-edit';
import {defineToolRegistryManifest} from '../../types/base-tool-types';

const toolName = 'edit-remote-file-exact' as const;

const argumentZod = z.object({
  path: z
    .string()
    .describe('The file path relative to the code component project root.'),
  intent: z
    .string()
    .optional()
    .describe('A short summary of the edit, shown to the user.'),
  edits: z
    .array(
      z.object({
        oldText: z
          .string()
          .min(1)
          .describe(
            'Exact text copied from the current file. Must identify one unique region.'
          ),
        newText: z
          .string()
          .describe(
            'Literal replacement text. Use an empty string to delete the matched text.'
          ),
      })
    )
    .min(1)
    .describe(
      'Non-overlapping replacements, all matched against the original file, not sequentially.'
    ),
});

export const editRemoteFileExactTool = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Edit component',
  prettyStatus: {
    executing: 'Editing component',
    executed: 'Edited component',
    failed: 'Failed to edit component',
    partial: 'Edited component with errors',
  },
  prettyErrors: {fallback: 'Unable to edit component.'},
  llmTool: {
    name: toolName,
    description: dedent`
      Edit an existing code component sandbox file using targeted text replacements.
      Read the current source with read-remote-file when it is not already available or may be stale.
      Copy oldText verbatim, including indentation, with enough context to match exactly once.
      Every edit targets the original file. Batch disjoint changes; combine overlapping changes into one replacement.
      Supply literal newText without omitted-code markers. Use write-remote-file for new files or complete rewrites.
      If a target is missing or ambiguous, reread the file and correct or expand oldText. Do not repeat the same invalid edit.
      Matching failures write nothing. Compilation errors mean the edit was written and the resulting source needs repair.
    `,
    inputSchema: argumentZod,
  },
  parseSchema: z.preprocess(prepareEditArguments, argumentZod),
  type: 'designer',
  permission: [{resource: 'SiteSymbol', action: 'modify'}],
  confirmBeforeExecuting: false,
  timeout: 60000,
});
